import { PanelBottomIcon, PanelRightIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import { Tooltip, TooltipTrigger, TooltipPopup } from "../ui/tooltip";

export function TerminalToggleButton({
  disabledReason,
  onToggle,
  open,
  hiddenRunningCount,
  tooltip,
}: {
  readonly disabledReason: string | null;
  readonly onToggle: (() => void) | undefined;
  readonly open: boolean;
  readonly hiddenRunningCount: number;
  readonly tooltip: string;
}) {
  if (!onToggle && !disabledReason) return null;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            aria-pressed={open}
            aria-label={
              hiddenRunningCount > 0
                ? `Toggle terminal drawer, ${hiddenRunningCount} running`
                : "Toggle terminal drawer"
            }
            aria-disabled={disabledReason !== null}
            onClick={disabledReason === null ? onToggle : undefined}
            data-testid="chatheader-terminal-toggle"
            className={cn(
              "relative inline-flex size-11 shrink-0 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring sm:size-8",
              open && "bg-muted/80 text-foreground",
            )}
          >
            {/* A bottom drawer, named by its glyph the way t3code does — a
                        terminal square says "a terminal", not "a panel that opens
                        from the bottom", which is what the control actually does. */}
            <PanelBottomIcon aria-hidden="true" className="size-4" />
            {hiddenRunningCount === 1 ? (
              <span
                aria-hidden="true"
                className="absolute right-1 top-1 size-1.5 rounded-full bg-success/90 ring-1 ring-background"
              />
            ) : hiddenRunningCount > 1 ? (
              <span
                aria-hidden="true"
                className="absolute -right-0.5 -top-0.5 flex h-3 min-w-3 items-center justify-center rounded-full bg-success/90 px-0.5 font-mono text-[9px] font-semibold leading-none text-success-foreground ring-1 ring-background tabular-nums"
              >
                {hiddenRunningCount}
              </span>
            ) : null}
          </button>
        }
      />
      <TooltipPopup side="bottom">{disabledReason ?? tooltip}</TooltipPopup>
    </Tooltip>
  );
}

/** The right sidebar's toggle, badged with the agents a closed panel hides. */
export function RightSidebarToggleButton({
  disabledReason,
  show,
  onToggle,
  hasActivePanel,
  hiddenAgentCount,
  label,
}: {
  readonly show: boolean;
  readonly disabledReason: string | null;
  readonly onToggle: (() => void) | undefined;
  readonly hasActivePanel: boolean;
  readonly hiddenAgentCount: number;
  readonly label: string;
}) {
  if (!show || (!onToggle && !disabledReason)) return null;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            aria-label={label}
            aria-pressed={hasActivePanel}
            aria-disabled={disabledReason !== null}
            onClick={disabledReason === null ? onToggle : undefined}
            data-testid="chatheader-panels-trigger"
            data-has-active={hasActivePanel ? "true" : "false"}
            className={cn(
              "relative inline-flex size-11 shrink-0 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring sm:size-8",
              hasActivePanel && "bg-muted/80 text-foreground",
            )}
          >
            <PanelRightIcon aria-hidden="true" className="size-4" />
            {hiddenAgentCount > 0 ? (
              <span
                aria-hidden="true"
                // Same shape as the terminal badge beside it, in the
                // info hue: fixed dark text on a fixed fill, so the pair
                // reads as one family and neither depends on the theme's
                // foreground resolving legibly over a saturated colour.
                className="absolute -right-0.5 -top-0.5 flex h-3 min-w-3 items-center justify-center rounded-full bg-info/90 px-0.5 font-mono text-[9px] font-semibold leading-none text-info-foreground ring-1 ring-background tabular-nums"
              >
                {hiddenAgentCount}
              </span>
            ) : null}
          </button>
        }
      />
      <TooltipPopup side="bottom">{disabledReason ?? label}</TooltipPopup>
    </Tooltip>
  );
}
